/**
 * AnimatedExposureCard — landing-page hero demo card.
 *
 * Single smooth animation: score climbs continuously from 8 (LOW · green)
 * to 84 (CRITICAL · red) over ~7 seconds, then snaps back and repeats.
 * Ring colour, glow, tier label, and stat values all derive from the
 * current score in realtime — no discrete holds.
 *
 * The faint gray track and the live coloured arc share an identical
 * stroke-dash pattern, so the gap geometry never appears to rotate.
 */
import React, { useEffect, useState, useRef } from "react";

// Colour stops — score positions where each tier colour peaks. Between
// stops, the renderer interpolates RGB so the ring smoothly cross-fades.
// Evenly distributed across 0-100 so each colour segment gets identical
// runtime. Don't tighten/loosen these without updating
// /app/frontend/src/components/ExposureSyncText.jsx in lockstep.
const COLOR_STOPS = [
  { at: 0,   color: "#1fdd83" },  // green — LOW
  { at: 33,  color: "#FFB000" },  // amber — MODERATE
  { at: 66,  color: "#FF8A1F" },  // orange — HIGH
  { at: 100, color: "#FF2A2A" },  // red — CRITICAL
];

// Tier label thresholds.
function tierForScore(s) {
  if (s < 25) return "LOW";
  if (s < 50) return "MODERATE";
  if (s < 70) return "HIGH";
  return "CRITICAL";
}

// Stat value endpoints — interpolated linearly with score.
const MIN_SCORE = 8;
const MAX_SCORE = 84;
const STAT_RANGE = {
  balAtRiskUsd: [1_200, 528_000],
  signedTx:     [12,    5_888],
};

// Animation timing.
const HOLD_MS  = 2500;   // full-green hold so both gauge + title sync up
const RAMP_MS  = 8000;   // smooth climb LOW → CRITICAL
const RESET_MS = 2000;   // quick reset back to LOW
const CYCLE_MS = HOLD_MS + RAMP_MS + RESET_MS;

const ARC_RADIUS = 92;
const ARC_CIRCUMFERENCE = 2 * Math.PI * ARC_RADIUS;       // ≈ 578
const ARC_VISIBLE = (ARC_CIRCUMFERENCE * 270) / 360;      // ≈ 433
// Both circles use the SAME dash pattern so they overlap perfectly:
//   ARC_VISIBLE on, then ARC_CIRCUMFERENCE off (effectively no repeat).
const DASH_PATTERN = `${ARC_VISIBLE} ${ARC_CIRCUMFERENCE}`;

// RGB lerp between two hex colours. Returns a {r,g,b} triple so callers
// can format as `rgb(...)` or `rgba(..., a)` without string surgery.
function lerpHex(a, b, t) {
  const ah = a.replace("#", "");
  const bh = b.replace("#", "");
  const ar = parseInt(ah.slice(0, 2), 16);
  const ag = parseInt(ah.slice(2, 4), 16);
  const ab = parseInt(ah.slice(4, 6), 16);
  const br = parseInt(bh.slice(0, 2), 16);
  const bg = parseInt(bh.slice(2, 4), 16);
  const bb = parseInt(bh.slice(4, 6), 16);
  return {
    r: Math.round(ar + (br - ar) * t),
    g: Math.round(ag + (bg - ag) * t),
    b: Math.round(ab + (bb - ab) * t),
  };
}

function rgb({ r, g, b }) {
  return `rgb(${r},${g},${b})`;
}

function rgba({ r, g, b }, a) {
  return `rgba(${r},${g},${b},${a})`;
}

// Pick the right pair of colour stops for the current score and lerp
// between them. Score is first remapped to a 0-100 progress so MIN_SCORE
// = pure green and MAX_SCORE = pure red — no mid-fade endpoints.
function colorForScore(s) {
  const span = (MAX_SCORE - MIN_SCORE) || 1;
  const p = Math.max(0, Math.min(100, ((s - MIN_SCORE) / span) * 100));
  if (p <= COLOR_STOPS[0].at)
    return lerpHex(COLOR_STOPS[0].color, COLOR_STOPS[0].color, 0);
  if (p >= COLOR_STOPS[COLOR_STOPS.length - 1].at) {
    const c = COLOR_STOPS[COLOR_STOPS.length - 1].color;
    return lerpHex(c, c, 0);
  }
  for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
    const a = COLOR_STOPS[i];
    const b = COLOR_STOPS[i + 1];
    if (p >= a.at && p <= b.at) {
      const t = (p - a.at) / (b.at - a.at);
      return lerpHex(a.color, b.color, t);
    }
  }
  return lerpHex(COLOR_STOPS[0].color, COLOR_STOPS[0].color, 0);
}

// Cubic ease-in-out for the climb so it doesn't feel mechanical.
function easeInOut(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

// Compact USD formatter — under $1K shows whole dollars, otherwise $K.
function fmtUsd(n) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

function fmtInt(n) {
  return Math.round(n).toLocaleString();
}

export default function AnimatedExposureCard() {
  const [score, setScore] = useState(MIN_SCORE);
  const rafRef = useRef(null);

  useEffect(() => {
    function tick(now) {
      // Use the global high-resolution clock so this component and any
      // sibling (e.g. ExposureSyncText in the hero title) compute the
      // exact same phase regardless of when they mounted.
      const elapsed = now % CYCLE_MS;
      let s;
      if (elapsed < HOLD_MS) {
        // Hold at LOW so the two synced components have a clear
        // shared anchor before the next ramp starts.
        s = MIN_SCORE;
      } else if (elapsed < HOLD_MS + RAMP_MS) {
        const r = (elapsed - HOLD_MS) / RAMP_MS;
        s = lerp(MIN_SCORE, MAX_SCORE, easeInOut(r));
      } else {
        const r = (elapsed - HOLD_MS - RAMP_MS) / RESET_MS;
        s = lerp(MAX_SCORE, MIN_SCORE, r);
      }
      setScore(s);
      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const displayScore = Math.round(score);
  const colorTriple = colorForScore(score);
  const color = rgb(colorTriple);              // solid for stroke + text
  const colorBorder = rgba(colorTriple, 0.85); // strong tint for stat borders
  const colorGlow = rgba(colorTriple, 0.55);   // soft halo for shadows
  const label = tierForScore(score);
  const dashOffset = ARC_VISIBLE * (1 - score / 100);

  // Score-driven stat interpolation. Maps the live score onto each stat's
  // [min, max] range so the numbers grow in lockstep with the ring.
  const tStat = (score - MIN_SCORE) / (MAX_SCORE - MIN_SCORE);
  const balAtRisk = lerp(STAT_RANGE.balAtRiskUsd[0], STAT_RANGE.balAtRiskUsd[1], tStat);
  const signedTx = lerp(STAT_RANGE.signedTx[0], STAT_RANGE.signedTx[1], tStat);
  // Address type promotes from plain EOA → EOA · 7702 once we cross HIGH.
  const addressType = score >= 55 ? "EOA · 7702" : "EOA";

  return (
    <div
      className="qt-card relative"
      data-testid="hero-demo-card"
      style={{
        padding: 0,
        overflow: "hidden",
        background: "#000000",
        border: "1px solid var(--qt-cyan)",
        boxShadow: "0 0 24px rgba(0,229,255,0.18)",
      }}
    >
      <div style={{ padding: 24, borderBottom: "1px solid rgba(0,229,255,0.25)" }}>
        <div className="qt-label" style={{ fontSize: 13 }}>EXPOSURE READOUT · LIVE DEMO</div>
        <div className="qt-mono mt-1" style={{ color: "var(--qt-text-3)", fontSize: 13 }}>
          0xd8dA…6045 · ETHEREUM
        </div>
      </div>
      <div className="px-6 py-8 flex flex-col items-center">
        <div style={{ position: "relative", width: 220, height: 220 }}>
          <svg width="220" height="220" viewBox="0 0 220 220">
            {/* Faint background track — fixed 270deg arc, never moves. */}
            <circle
              cx="110"
              cy="110"
              r={ARC_RADIUS}
              fill="none"
              stroke="rgba(255,255,255,0.10)"
              strokeWidth="6"
              strokeDasharray={DASH_PATTERN}
              strokeDashoffset={0}
              transform="rotate(135 110 110)"
              strokeLinecap="round"
            />
            {/* Live coloured arc — overlays the track exactly. */}
            <circle
              cx="110"
              cy="110"
              r={ARC_RADIUS}
              fill="none"
              stroke={color}
              strokeWidth="6"
              strokeDasharray={DASH_PATTERN}
              strokeDashoffset={dashOffset}
              transform="rotate(135 110 110)"
              strokeLinecap="round"
              style={{
                filter: `drop-shadow(0 0 8px ${colorGlow})`,
              }}
            />
          </svg>
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div className="qt-label" style={{ color: "#FFFFFF" }}>EXPOSURE</div>
            <div
              data-testid="hero-demo-score"
              className="qt-mono"
              style={{
                fontSize: 56,
                fontWeight: 700,
                lineHeight: 1,
                color,
                textShadow: `0 0 16px ${colorGlow}`,
              }}
            >
              {displayScore}
            </div>
            <div
              data-testid="hero-demo-tier"
              className="qt-mono"
              style={{
                fontSize: 11,
                letterSpacing: "0.25em",
                color,
                fontWeight: 700,
                marginTop: 6,
              }}
            >
              {label}
            </div>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3 w-full">
          <Stat label="BAL AT RISK"  value={fmtUsd(balAtRisk)} accent={colorBorder} glow={colorGlow} />
          <Stat label="SIGNED TX"    value={fmtInt(signedTx)}  accent={colorBorder} glow={colorGlow} />
          <Stat label="ADDRESS TYPE" value={addressType}       accent={colorBorder} glow={colorGlow} />
          <Stat label="SCHEME"       value="secp256k1"         accent={colorBorder} glow={colorGlow} />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, glow }) {
  return (
    <div
      className="qt-card"
      style={{
        padding: 12,
        background: "#000000",
        // Inline overrides .qt-card's default border so the tint matches
        // the live score colour. Strong opacity (0.85) so green/amber
        // phases are clearly visible against the dark background.
        border: `1px solid ${accent}`,
        boxShadow: `0 0 12px -2px ${glow}, inset 0 0 18px -8px ${glow}`,
      }}
    >
      <div className="qt-label">{label}</div>
      <div className="qt-mono text-base mt-1" style={{ color: "#fff" }}>{value}</div>
    </div>
  );
}
